import { Model } from '@polleverywhere/backbone'
import activityFactory from '@polleverywhere/graphql-models/lib/activityFactory'
import { FetchActivityError } from './FetchActivityError'
import { UnknownActivityError } from './UnknownActivityError'

export const REGISTRATION_STATUS = {
  OK: 'ok',
  UNPAID: 'unpaid',
  REQUIRED: 'required',
  SUGGESTED: 'suggested',
  CONTACT: 'contact'
}

// Only prompt for MCPs that are graded and not anonymous
function activitySuggestsRegistration (activity) {
  return activity &&
    !activity.get('anonymous') &&
    (activity.get('type') !== 'competition' && !activity.competition) &&
    !!activity.get('grading_enabled')
}

/**
 * These are properties computed from other values in the state
 *
 * The `compute` function returns the current value and should be
 * 'pure' to not cause any side effects
 */
const COMPUTED = {
  screenNamesEnabled: {
    listenTo () {
      return [
        [this.presenter.registrationInfo, 'change:screen_names_enabled change:competition_screen_names_enabled'],
        [this, 'change:currentActivity change:currentActivity:anonymous change:currentActivity:polls_count']
      ]
    },
    compute () {
      const presenterEnabled = !!this.presenter.registrationInfo.get('screen_names_enabled')

      // Nothing is active, so just base it on the presenter setting
      if (!this.currentActivity) {
        return presenterEnabled
      }

      const competitionEnabled = !!this.presenter.registrationInfo.get('competition_screen_names_enabled')

      // Wouldn't be active for competition either
      if (!presenterEnabled && !competitionEnabled) {
        return false
      }

      const currentType = this.currentActivity?.type()
      const anonymous = currentType === 'survey'
        ? this.currentActivity.polls.models.some(poll => !!poll.get('anonymous'))
        : !!this.currentActivity.get('anonymous')

      // No screen names when anonymous
      if (anonymous) {
        return false
      }

      // Now that we know we have an activity and competition screen names are
      // enabled, we just need to see if we're doing a competition. If we are,
      // screen names are enabled.
      const isCompetition = currentType === 'competition'
      const inCompetition = !!this.currentActivity.competition

      return isCompetition || inCompetition ? true : presenterEnabled
    }
  },
  registrationStatus: {
    listenTo () {
      return [
        [this, 'change:registrationForced'],
        [this.presenter.registrationInfo, 'change'],
        [this, 'change:currentActivity change:currentActivity:registered_participants_only change:currentActivity:grading_enabled  change:currentActivity:anonymous'],
        [this, 'change:pinnedActivity change:pinnedActivity:registered_participants_only change:pinnedActivity:grading_enabled  change:pinnedActivity:anonymous']
      ]
    },
    compute () {
      const registrationInfo = this.presenter.registrationInfo
      const isRegistered = registrationInfo.isExistingParticipant()
      const registeredParticipantsOnly = this.registrationForced ||
        !!this.currentActivity?.requiresRegistration?.() ||
        !!this.pinnedActivity?.requiresRegistration?.()
      const participantSelfRegistration = registrationInfo.participantSelfRegistration()
      const ownPollEvPage = this.user.get('login') === registrationInfo.get('login')
      const unpaid = registrationInfo.needsToPay()
      const registrationSuggested = !!registrationInfo.get('registration_suggested') &&
        !isRegistered &&
        activitySuggestsRegistration(this.currentActivity)

      // Figure out what to show based on the user and model's status
      const showPaywall = !registeredParticipantsOnly && isRegistered && unpaid && !ownPollEvPage
      const showRegisteredParticipantsOnly = registeredParticipantsOnly && !isRegistered && participantSelfRegistration
      const showContactPresenterToRegister = registeredParticipantsOnly && !isRegistered && !participantSelfRegistration

      const unpaidAndUnregistered = !isRegistered && unpaid
      const showRegisterForCredit = !registeredParticipantsOnly && (unpaidAndUnregistered || registrationSuggested)

      if (showPaywall) {
        return REGISTRATION_STATUS.UNPAID
      } else if (showRegisteredParticipantsOnly) {
        return REGISTRATION_STATUS.REQUIRED
      } else if (showContactPresenterToRegister) {
        return REGISTRATION_STATUS.CONTACT
      } else if (showRegisterForCredit) {
        return REGISTRATION_STATUS.SUGGESTED
      }

      return REGISTRATION_STATUS.OK
    }
  },
  exposeLogout: {
    listenTo () {
      return [
        [this.user, 'change:id'],
        [this, 'change:role']
      ]
    },
    compute () {
      return this.user?.isLoggedIn() && this.role === 'embed'
    }
  }
}

/**
 * These are will run when any `listenTo` property changes and on init if
 * `initial` is true
 *
 * The `run` function does any work needed, be it fetching or logging data
 */
const AUTORUN = {
  fetchPinnedActivity: {
    initial: true,
    listenTo () {
      return [
        [this.presenter, 'change:pinned_participant_key'],
        [this, 'change:disablePinnedActivity']
      ]
    },
    async run () {
      const participantKey = this.presenter.get('pinned_participant_key')

      if (this.disablePinnedActivity || !participantKey) {
        return
      }

      if (this.pinnedActivity?.get('participant_key') === participantKey) {
        return
      }

      const model = await activityFactory({
        type: 'discourse',
        participant_key: participantKey
      })
      await model?.fetch()

      this.set('pinnedActivity', model)
    }
  },
  logTogglePinned: {
    initial: false,
    listenTo () {
      return [
        [this, 'change:showPinnedActivity']
      ]
    },
    run () {
      this.trigger('view:action', `toggle:show_pinned_activity:${this.showPinnedActivity ? 'true' : 'false'}`)
    }
  }
}

function proxySubmodelChanges (prop) {
  this.on(`change:${prop}`, (state, newActivity) => {
    const previousActivity = state.previous(prop)
    if (previousActivity) {
      this.stopListening(previousActivity)
    }
    if (newActivity) {
      this.listenTo(newActivity, 'change', (model) => {
        for (const [key, value] of Object.entries(model.changedAttributes())) {
          this.trigger(`change:${prop}:${key}`, model, value)
        }
      })
    }
  })
}

/**
 * Common state for response pages (e.g. Current and Web)
 *
 * So that we can keep everything in sync without passing events around everywhere.
 *
 * Based on MobX conventions, adapted to work with Backbone (waiting on MobX 6 and
 * larger refactor). Getters should be used to access properties, and `set` should
 * never be called from outside this class, use 'actions' (methods) to change state
 */
export default class ResponsePageState extends Model {
  get defaults () {
    return {
      role: undefined,
      user: undefined,
      presenter: undefined,
      participant: undefined,
      relationship: undefined,
      currentActivity: undefined,
      screenNamesEnabled: false,
      pinnedActivity: undefined,
      showPinnedActivity: false,
      showScreenNameEdit: false,
      disablePinnedActivity: false,
      registrationStatus: REGISTRATION_STATUS.OK,
      registrationForced: false
    }
  }

  get role () {
    return this.get('role')
  }

  get user () {
    return this.get('user')
  }

  get presenter () {
    return this.get('presenter')
  }

  get participant () {
    return this.get('participant')
  }

  get relationship () {
    return this.get('relationship')
  }

  get currentActivity () {
    return this.get('currentActivity')
  }

  get pinnedActivity () {
    return this.get('pinnedActivity')
  }

  get showPinnedActivity () {
    return this.get('showPinnedActivity')
  }

  get screenNamesEnabled () {
    return this.get('screenNamesEnabled')
  }

  get showScreenNameEdit () {
    return this.get('showScreenNameEdit')
  }

  get disablePinnedActivity () {
    return this.get('disablePinnedActivity')
  }

  get registrationStatus () {
    return this.get('registrationStatus')
  }

  get registrationForced () {
    return this.get('registrationForced')
  }

  initialize () {
    if (!this.user) { throw new Error('requires user') }
    if (!this.presenter) { throw new Error('requires presenter') }
    if (!this.relationship) { throw new Error('requires relationship') }

    proxySubmodelChanges.call(this, 'currentActivity')
    proxySubmodelChanges.call(this, 'pinnedActivity')

    for (const [attr, def] of Object.entries(COMPUTED)) {
      def.listenTo.call(this).forEach(([obj, listener]) => {
        this.listenTo(obj, listener, () => this.set(attr, def.compute.call(this)))
      })
      this.set(attr, def.compute.call(this))
    }

    for (const [, def] of Object.entries(AUTORUN)) {
      def.listenTo.call(this).forEach(([obj, listener]) => {
        this.listenTo(obj, listener, () => def.run.call(this))
      })
      if (def.initial) {
        def.run.call(this)
      }
    }
  }

  async changeCurrentActivity (type, participantKey) {
    const model = await activityFactory({
      type,
      participant_key: participantKey
    })

    if (!model) {
      this.unset('currentActivity')
      throw new UnknownActivityError(`Unknown activity for '${type}'`)
    }

    try {
      const include = type === 'survey' ? 'poll_instances' : null
      await model.fetch({
        data: {
          include,
          exclude: 'flash_options,visual_settings'
        }
      })
    } catch (xhrError) {
      this.unset('currentActivity')
      throw new FetchActivityError('Cannot fetch activity', `${type}:${participantKey}`, xhrError.status)
    }

    this.set('currentActivity', model)
  }

  clearCurrentActivity () {
    this.unset('currentActivity')
  }

  togglePinnedActivity (shouldBeActive) {
    if (!this.disablePinnedActivity) {
      this.set('showPinnedActivity', shouldBeActive ?? !this.showPinnedActivity)
    }
  }

  toggleScreenNameEdit (shouldBeActive) {
    if (!this.screenNamesEnabled) {
      shouldBeActive = false
    }
    this.set('showScreenNameEdit', shouldBeActive ?? !this.showScreenNameEdit)
  }

  forceRegistration (forced = false) {
    this.set('registrationForced', !!forced)
  }
}
